import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Divider, Form, Grid, GridColumn, GridRow, Icon, Modal } from "semantic-ui-react";

import { Can, DealersAndLocationsDropdown, UploadFilesZone } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, DEALER_DOCUMENTATION_CATEGORIES } from "models";
import "modules/DealerDocumentations/components/AddEditDealerDocumentationModal/AddEditDealerDocumentationModal.scss";
import { ImagePreview } from "modules/DealerDocumentations/components/AddEditDealerDocumentationModal/components/ImagePreview";
import { useUploadDocumentationFileMutation } from "modules/DealerDocumentations/components/AddEditDealerDocumentationModal/hooks";
import { enumToDropdownOptions, isFileWithinSizeLimit } from "utils/common";

import type { DealersLocationsDropdownValue } from "components";
import type { DealerDocumentation, DealerID, DealerLocationID } from "models";
import type ReactQuill from "react-quill";

// eslint-disable-next-line import/order
import "react-quill/dist/quill.snow.css";

export type DealerDocumentationFormValues = Pick<DealerDocumentation, "title" | "content" | "url" | "is_pinned" | "visible_to_mechanic" | "category_id"> & {
  publish_date: Date | null;
  dealer_ids: DealerID[];
  dealer_location_ids: DealerLocationID[];
};

type DealerDocumentationFormProps = {
  documentation: DealerDocumentation;
  isCreateDocumentationPending: boolean;
  isUpdateDocumentationPending: boolean;
  closeModal: () => void;
  setShowDeleteConfirmation: (value: boolean) => void;
  onSubmit: (formValues: DealerDocumentationFormValues) => void;
};

type FileInputEvent = React.ChangeEvent<HTMLInputElement> | React.DragEvent;

const FormHelper = createFormHelper<DealerDocumentationFormValues>();

const isGifFile = (file: File) => file.type === "image/gif" || file.name.toLowerCase().endsWith(".gif");
const getFileFromEvent = (e: FileInputEvent) => {
  if ("dataTransfer" in e) {
    e.preventDefault();
    return e.dataTransfer.files.item(0);
  }

  return e.target.files?.item(0) ?? null;
};

export const DealerDocumentationForm = ({
  documentation,
  isCreateDocumentationPending,
  isUpdateDocumentationPending,
  closeModal,
  onSubmit,
  setShowDeleteConfirmation
}: DealerDocumentationFormProps) => {
  const { t } = useTranslation();
  const { watch, setValue, formState, handleSubmit } = FormHelper.useFormContext();

  const { user } = usePermanentRealTimeUser();
  const quillRef = useRef<ReactQuill>(null);
  const { mutateAsync: uploadDocumentationFile, isPending: isUploadDocumentationFilePending } = useUploadDocumentationFileMutation();

  const [url, title, content, publishDate, dealerIds, dealerLocationIds] = watch(["url", "title", "content", "publish_date", "dealer_ids", "dealer_location_ids"]);

  const insertImageInContent = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png, image/jpeg, image/bmp, image/x-icon";

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file || !isFileWithinSizeLimit(file, t)) return;

      if (isGifFile(file)) {
        toast.error(t("v8_gif_files_not_allowed", "GIF files are not allowed"));
        return;
      }

      const editor = quillRef.current?.getEditor();
      if (!editor) return;

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== "string") return;

        const { index } = editor.getSelection(true);
        editor.insertEmbed(index, "image", reader.result, "user");
        editor.setSelection(index + 1, 0, "user");
      };
      reader.readAsDataURL(file);
    };

    input.click();
  }, [t]);

  const quillModules = useMemo(
    () => ({ toolbar: { container: ["bold", "italic", "underline", "link", "image", "video"], handlers: { image: insertImageInContent } } }),
    [insertImageInContent]
  );

  const isAuthorizedToDelete = () => {
    if (!user || documentation.id < 0) return false;
    if ((documentation.created_by && user.role_id < documentation.created_by.role_id) || user.id === documentation.created_by?.id) return true;
  };

  const handleDealersLocationsChange = ({ dealerIds: dealer_ids, locationIds: dealer_location_ids }: DealersLocationsDropdownValue) => {
    setValue("dealer_ids", dealer_ids);
    setValue("dealer_location_ids", dealer_location_ids);
  };

  const onFileChange = async (event: FileInputEvent) => {
    const file = getFileFromEvent(event);
    if (!file || !isFileWithinSizeLimit(file, t)) return;

    if (isGifFile(file)) {
      toast.error(t("v8_gif_files_not_allowed", "GIF files are not allowed"));
      return;
    }

    const result = await uploadDocumentationFile(file);
    setValue("url", result);
  };

  return (
    <>
      <Modal.Header className="padding-container">
        <h3 className="documentation-modal-title">
          {documentation.id ? t("v8_edit_documentation", "Edit Documentation") : t("v8_add_documentation", "Add documentation")}
        </h3>
      </Modal.Header>
      <Divider fitted />

      <Modal.Content className="padding-container" scrolling>
        <Grid>
          <GridRow>
            <GridColumn>
              <Form.Field required error={formState.isSubmitted && !title}>
                <FormHelper.Field.Input name="title" inputProps={{ placeholder: t("v8_title", "Title") }} />
              </Form.Field>
            </GridColumn>
          </GridRow>
          <GridRow>
            <GridColumn width={3}>
              <Form.Field required>
                <FormHelper.Field.Dropdown
                  name="category_id"
                  options={enumToDropdownOptions({ enumObj: DEALER_DOCUMENTATION_CATEGORIES, t })}
                  dropdownProps={{ fluid: true, noResultsMessage: t("v8_no_categories", "No categories"), placeholder: t("v8_select_category", "Select a category") }}
                />
              </Form.Field>
            </GridColumn>
            <GridColumn width={6}>
              <Form.Field required error={formState.isSubmitted && !dealerIds.length && !dealerLocationIds.length}>
                <DealersAndLocationsDropdown values={{ dealerIds, locationIds: dealerLocationIds }} handleOnChange={handleDealersLocationsChange} />
              </Form.Field>
            </GridColumn>
            <GridColumn width={3}>
              <Form.Field required error={formState.isSubmitted && !publishDate}>
                <FormHelper.Field.DatePicker name="publish_date" fluid />
              </Form.Field>
            </GridColumn>
            <GridColumn width={1}>
              <FormHelper.Field.Checkbox name="is_pinned" label={t("v8_pin", "Pin")} />
            </GridColumn>
            <GridColumn width={3}>
              <FormHelper.Field.Checkbox name="visible_to_mechanic" label={t("v8_visible_to_mechanic", "Visible to Mechanic")} checkboxProps={{ toggle: true }} />
            </GridColumn>
          </GridRow>
          <GridRow>
            <GridColumn>
              <FormHelper.Field.Quill
                name="content"
                quillRef={quillRef}
                quillProps={{ modules: quillModules, className: formState.isSubmitted && !content ? "quill-error" : "" }}
              />
            </GridColumn>
          </GridRow>
          <GridRow>
            <GridColumn>
              <Form.Field>
                <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.DealerDocumentationFiles}>
                  <UploadFilesZone
                    onFileInputChange={onFileChange}
                    onFileDragOver={e => e.preventDefault()}
                    onFileDrop={onFileChange}
                    display={!url}
                    hasError={false}
                    isUploading={isUploadDocumentationFilePending}
                  />
                </Can>
                {url && (
                  <ImagePreview url={url}>
                    <Icon className="trash" onClick={() => setValue("url", null)} />
                  </ImagePreview>
                )}
              </Form.Field>
            </GridColumn>
          </GridRow>
        </Grid>
      </Modal.Content>
      <Divider fitted />
      <Modal.Actions className="padding-container">
        <div className="action-buttons-container">
          {isAuthorizedToDelete() && (
            <Can I={ACL_ACTIVITIES.Delete} the={ACL_RESOURCES.DealerDocumentation}>
              <Button
                type="button"
                color="red"
                onClick={() => setShowDeleteConfirmation(true)}
                disabled={isUploadDocumentationFilePending || isCreateDocumentationPending || isUpdateDocumentationPending}
              >
                {t("v8_delete", "Delete")}
                <Icon className="trash" />
              </Button>
            </Can>
          )}
          <div className="save-cancel-buttons-container">
            <Button type="button" className="light" onClick={closeModal}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark outline" disabled={isUploadDocumentationFilePending || isCreateDocumentationPending || isUpdateDocumentationPending} />
            </Button>
            <Can I={[ACL_ACTIVITIES.Create, ACL_ACTIVITIES.Update]} the={ACL_RESOURCES.DealerDocumentation}>
              <Button
                color="green"
                disabled={isUploadDocumentationFilePending || isCreateDocumentationPending || isUpdateDocumentationPending}
                loading={isUploadDocumentationFilePending || isCreateDocumentationPending || isUpdateDocumentationPending}
                onClick={handleSubmit(onSubmit)}
              >
                {t("v8_save", "Save")} <Icon className="check" />
              </Button>
            </Can>
          </div>
        </div>
      </Modal.Actions>
    </>
  );
};
